/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { DOMConversionMap, DOMConversionOutput, DOMExportOutput, EditorConfig, LexicalCommand, LexicalNode, NodeKey, SerializedLexicalNode } from "lexical";
import type { JSX } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { addClassNamesToElement, mergeRegister, removeClassNamesFromElement } from "@lexical/utils";
import {
  $applyNodeReplacement,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DecoratorNode,
  KEY_DELETE_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_ENTER_COMMAND,
  $setSelection,
  $getNodeByKey,
  $createRangeSelection,
} from "lexical";
import * as React from "react";
import { useCallback, useEffect, useRef } from "react";
import { $createSlidParagraphNode } from "../SlidParagraphNode";
import styled from "styled-components";
import { FOCUS_HORIZONTAL_RULE_DUMMY_TEXTAREA_COMMAND } from "components/NewEditor/plugins/HorizontalRulePlugin";
import { useAppSelector } from 'hooks';

export type SerializedHorizontalRuleNode = SerializedLexicalNode;

export const INSERT_HORIZONTAL_RULE_COMMAND: LexicalCommand<void> = createCommand("INSERT_HORIZONTAL_RULE_COMMAND");

function HorizontalRuleComponent({ nodeKey }: { nodeKey: NodeKey }) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const dummyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const applicationType = useAppSelector((state) => state.slidGlobal.applicationType);

  const $onDelete = useCallback(
    (event: KeyboardEvent) => {
      const deleteSelection = $getSelection();
      if (isSelected && $isNodeSelection(deleteSelection)) {
        event.preventDefault();
        editor.update(() => {
          deleteSelection.getNodes().forEach((node) => {
            if ($isHorizontalRuleNode(node)) {
              node.remove();
            }
          });
        });
      }
      return false;
    },
    [isSelected, editor]
  );

  const $onEnter = useCallback(
    (event: KeyboardEvent) => {
      editor.update(() => {
        const selection = $getSelection();
        if (isSelected && $isNodeSelection(selection) && selection.getNodes().length === 1) {
          selection.getNodes().forEach((node) => {
            if ($isHorizontalRuleNode(node)) {
              if (dummyTextareaRef.current) {
                dummyTextareaRef.current.blur();
              }
              event.preventDefault();
              setSelected(false);
              editor.update(() => {
                const paragraphNode = $createSlidParagraphNode();
                const horizontalRuleNode = $getNodeByKey(nodeKey);

                if (horizontalRuleNode) {
                  horizontalRuleNode.insertAfter(paragraphNode);
                  paragraphNode.select();
                }
              });
            }
          });
        }
      });
      return false;
    },
    [isSelected, editor]
  );

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CLICK_COMMAND,
        (event: MouseEvent) => {
          const hrElem = editor.getElementByKey(nodeKey);

          if (event.target === hrElem) {
            if (!event.shiftKey) {
              clearSelection();
            }
            setSelected(!isSelected);
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW
      ),

      editor.registerCommand(KEY_DELETE_COMMAND, $onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, $onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ENTER_COMMAND, $onEnter, COMMAND_PRIORITY_LOW)
    );
  }, [clearSelection, editor, isSelected, nodeKey, $onDelete, setSelected]);

  useEffect(() => {
    const hrElem = editor.getElementByKey(nodeKey);
    const isSelectedClassName = "selected";

    if (hrElem !== null) {
      if (isSelected) {
        addClassNamesToElement(hrElem, isSelectedClassName);
      } else {
        removeClassNamesFromElement(hrElem, isSelectedClassName);
      }
    }
  }, [editor, isSelected, nodeKey]);

  useEffect(() => {
    if (isSelected && applicationType === "slidpocket") {
      editor.dispatchCommand(FOCUS_HORIZONTAL_RULE_DUMMY_TEXTAREA_COMMAND, undefined);
    }
  }, [isSelected, editor, applicationType]);

  return (
    <DummyTextarea
      ref={dummyTextareaRef}
      onKeyUp={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.key === "Enter") {
          $onEnter(e as unknown as KeyboardEvent);
        }
      }}
    />
  );
}

const DummyTextarea = styled.textarea`
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  opacity: 0;
`;

export class HorizontalRuleNode extends DecoratorNode<JSX.Element> {
  static getType(): string {
    return "horizontalrule";
  }

  static clone(node: HorizontalRuleNode): HorizontalRuleNode {
    return new HorizontalRuleNode(node.__key);
  }

  static importJSON(serializedNode: SerializedHorizontalRuleNode): HorizontalRuleNode {
    return $createHorizontalRuleNode().updateFromJSON(serializedNode);
  }

  static importDOM(): DOMConversionMap | null {
    return {
      hr: () => ({
        conversion: $convertHorizontalRuleElement,
        priority: 0,
      }),
    };
  }

  exportDOM(): DOMExportOutput {
    return { element: document.createElement("hr") };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = document.createElement("hr");
    addClassNamesToElement(element, config.theme.hr);
    return element;
  }

  getTextContent(): string {
    return "\n";
  }

  isInline(): false {
    return false;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return <HorizontalRuleComponent nodeKey={this.__key} />;
  }
}

function $convertHorizontalRuleElement(): DOMConversionOutput {
  return { node: $createHorizontalRuleNode() };
}

export function $createHorizontalRuleNode(): HorizontalRuleNode {
  return $applyNodeReplacement(new HorizontalRuleNode());
}

export function $isHorizontalRuleNode(node: LexicalNode | null | undefined): node is HorizontalRuleNode {
  return node instanceof HorizontalRuleNode;
}
